import { useEffect, useState } from 'react';

const LocationRelationship = () => {
  // Configuration controls
  const [bayCount, setBayCount] = useState(5);
  const [levelCount, setLevelCount] = useState(5);
  const [locationSizeVarietyCount, setLocationSizeVarietyCount] = useState(3);
  const [viewScale, setViewScale] = useState(100);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Relationship controls
  const [relationshipType, setRelationshipType] = useState('physical');
  const [proximityDirection, setProximityDirection] = useState('both');
  const [semanticRelation, setSemanticRelation] = useState('sameLevel');
  const [skipDistance, setSkipDistance] = useState(0);
  const [maxDistance, setMaxDistance] = useState(2);
  const [minDistance, setMinDistance] = useState(0);
  const [categoryCount, setCategoryCount] = useState(3);

  // Visualization states
  const [highlightedLocations, setHighlightedLocations] = useState([]);
  const [relationshipLines, setRelationshipLines] = useState([]);

  // Add new state for heatmap mode
  const [showHeatmap, setShowHeatmap] = useState(false);

  // Create bay structure
  const generateShelfStructure = () => {
    const bays = [];
    const levels = [];
    const bayColors = [
      'rgb(59, 130, 246)', // Blue
      'rgb(16, 185, 129)', // Green
      'rgb(245, 158, 11)', // Amber
      'rgb(239, 68, 68)', // Red
      'rgb(139, 92, 246)', // Purple
      'rgb(236, 72, 153)', // Pink
      'rgb(245, 158, 11)', // Orange
      'rgb(6, 182, 212)', // Cyan
      'rgb(168, 85, 247)', // Violet
      'rgb(249, 115, 22)', // Orange-red
    ];

    const levelColors = [
      'rgba(249, 115, 22, 0.15)', // Orange tint
      'rgba(16, 185, 129, 0.15)', // Green tint
      'rgba(59, 130, 246, 0.15)', // Blue tint
      'rgba(245, 158, 11, 0.15)', // Amber tint
      'rgba(192, 132, 252, 0.15)', // Purple tint
      'rgba(236, 72, 153, 0.15)', // Pink tint
      'rgba(245, 158, 11, 0.15)', // Orange tint
      'rgba(6, 182, 212, 0.15)', // Cyan tint
      'rgba(168, 85, 247, 0.15)', // Violet tint
      'rgba(249, 115, 22, 0.15)', // Orange-red tint
    ];

    // Generate bays with increased spacing
    for (let i = 0; i < bayCount; i++) {
      bays.push({
        id: `Bay-${(i + 1).toString().padStart(2, '0')}`,
        startCol: i * 1.1,
        endCol: (i + 1) * 1.1,
        color: bayColors[i % bayColors.length],
      });
    }

    // Generate levels
    for (let i = 0; i < levelCount; i++) {
      levels.push({
        id: `Level-${i + 1}`,
        startRow: i,
        endRow: i + 1,
        color: levelColors[i % levelColors.length],
      });
    }

    return { bays, levels };
  };

  const [shelfStructure, setShelfStructure] = useState(
    generateShelfStructure(),
  );

  // Update the location size templates to better fill levels
  const locationSizeTemplates = [
    // Default full-cell size
    { width: 1, height: 1 },
    // Wide locations
    { width: 2, height: 1 },
    { width: 3, height: 1 },
    // Tall locations
    { width: 1, height: 2 },
    // Large locations
    { width: 2, height: 1 },
    { width: 3, height: 1 },
    { width: 4, height: 1 },
  ];

  // Generate categories (A, B, C, ...)
  const generateCategories = count => {
    return Array.from({ length: count }, (_, i) => String.fromCharCode(65 + i));
  };

  // Update getHeatmapColor to handle opacity differently for categories
  const getHeatmapColor = (location, selectedLoc) => {
    if (!selectedLoc) return null;

    switch (relationshipType) {
      case 'physical': {
        const distance = calculateDistance(
          selectedLoc,
          location,
          proximityDirection,
        );
        const normalizedDistance = Math.min(distance / maxDistance, 1);
        const r = Math.round(255 * (1 - normalizedDistance));
        const g = Math.round(128 * (1 - normalizedDistance));
        const b = Math.round(255 * normalizedDistance);
        return `rgba(${r}, ${g}, ${b}, 0.7)`;
      }
      case 'semantic': {
        const sameBay = location.bay === selectedLoc.bay;
        const sameLevel = location.level === selectedLoc.level;

        if (semanticRelation === 'both' && sameBay && sameLevel) {
          return 'rgba(236, 72, 153, 0.7)'; // Pink for both
        } else if (semanticRelation === 'sameBay' && sameBay) {
          return 'rgba(249, 115, 22, 0.7)'; // Orange for same bay
        } else if (semanticRelation === 'sameLevel' && sameLevel) {
          return 'rgba(239, 68, 68, 0.7)'; // Red for same level
        }
        return 'rgba(209, 213, 219, 0.3)'; // Gray for no relation
      }
      case 'category': {
        // Don't apply any background color in category mode
        return null;
      }
      default:
        return null;
    }
  };

  // Generate locations with better width filling and bay constraints
  const generateLocations = () => {
    const locationsArray = [];
    const categories = generateCategories(categoryCount);
    let locationCounter = 1;

    // Track locations per bay
    const bayLocations = {};
    shelfStructure.bays.forEach(bay => {
      bayLocations[bay.id] = 0;
    });

    // Fill levels with locations
    shelfStructure.levels.forEach(level => {
      shelfStructure.bays.forEach(bay => {
        // Skip if bay already has max locations
        if (bayLocations[bay.id] >= 9) return;

        // Determine how many locations to create in this bay-level
        // More likely to create multiple locations if bay has few locations
        const remainingSpace = 9 - bayLocations[bay.id];
        const locationsToCreate = Math.min(
          remainingSpace,
          1 + Math.floor(Math.random() * 3), // 1-3 locations per level
        );

        // Calculate width for each location
        const locationWidth = 1 / locationsToCreate;

        for (let i = 0; i < locationsToCreate; i++) {
          locationsArray.push({
            id: `Loc-${locationCounter++}`,
            row: level.startRow,
            col: bay.startCol + i * locationWidth,
            width: locationWidth,
            height: 1,
            category: categories[Math.floor(Math.random() * categories.length)],
            bay: bay.id,
            level: level.id,
          });
          bayLocations[bay.id]++;
        }
      });
    });

    return locationsArray;
  };

  // State for locations
  const [locations, setLocations] = useState([]);

  // Regenerate shelf and locations when configuration changes
  useEffect(() => {
    const newStructure = generateShelfStructure();
    setShelfStructure(newStructure);
    setSelectedLocation(null);
    setHighlightedLocations([]);
    setRelationshipLines([]);
  }, [bayCount, levelCount]);

  // Generate locations when structure changes
  useEffect(() => {
    setLocations(generateLocations());
  }, [shelfStructure, locationSizeVarietyCount, categoryCount]);

  // Update distance calculation to properly handle diagonals
  const calculateDistance = (loc1, loc2, direction = 'both') => {
    const center1 = {
      x: loc1.col + loc1.width / 2,
      y: loc1.row + loc1.height / 2,
    };

    const center2 = {
      x: loc2.col + loc2.width / 2,
      y: loc2.row + loc2.height / 2,
    };

    const dx = Math.abs(center1.x - center2.x);
    const dy = Math.abs(center1.y - center2.y);

    if (direction === 'x-axis') {
      return dx;
    } else if (direction === 'y-axis') {
      return dy;
    } else {
      // For 'both', use Euclidean distance for true diagonal distance
      return Math.sqrt(dx * dx + dy * dy);
    }
  };

  // Add helper function to determine relative position
  const getRelativePosition = (selectedLoc, loc) => {
    const selectedCenter = selectedLoc.row + selectedLoc.height / 2;
    const locCenter = loc.row + loc.height / 2;

    if (Math.abs(selectedCenter - locCenter) < 0.1) return 'same';
    return locCenter < selectedCenter ? 'above' : 'below';
  };

  // Update getRelationshipDescription to include vertical position in all modes
  const getRelationshipDescription = (location, selectedLoc) => {
    if (!selectedLoc) return '';

    const distance = calculateDistance(
      selectedLoc,
      location,
      proximityDirection,
    );
    const position = getRelativePosition(selectedLoc, location);
    const positionSymbol =
      position === 'above' ? '↑' : position === 'below' ? '↓' : '•';
    const directionText =
      proximityDirection === 'x-axis'
        ? '(L/R)'
        : proximityDirection === 'y-axis'
          ? '(T/B)'
          : '';
    const levelDiff = Math.abs(
      parseInt(location.level.split('-')[1]) -
        parseInt(selectedLoc.level.split('-')[1]),
    );

    switch (relationshipType) {
      case 'physical': {
        const verticalInfo = position === 'same' ? '' : ` L${levelDiff}`;
        if (distance < minDistance) {
          return `Too close${verticalInfo} ${positionSymbol} ${directionText} (${distance.toFixed(1)})`;
        } else if (maxDistance < 10 && distance > maxDistance) {
          return `Too far${verticalInfo} ${positionSymbol} ${directionText} (${distance.toFixed(1)})`;
        } else if (
          skipDistance > 0 &&
          areLocationsAdjacent(selectedLoc, location, proximityDirection)
        ) {
          return `Adjacent${verticalInfo} ${positionSymbol} ${directionText} (skipped)`;
        }
        return `Distance${verticalInfo} ${positionSymbol} ${directionText}: ${distance.toFixed(1)}`;
      }

      case 'semantic': {
        const sameBay = location.bay === selectedLoc.bay;
        const sameLevel = location.level === selectedLoc.level;
        const isAbove = position === 'above';
        const isBelow = position === 'below';

        // For Top/Bottom direction, focus on vertical relationships
        if (proximityDirection === 'y-axis') {
          if (semanticRelation === 'above') {
            if (!isAbove) return 'Not above';
            return `Above: L${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          } else if (semanticRelation === 'below') {
            if (!isBelow) return 'Not below';
            return `Below: L${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          } else if (semanticRelation === 'both') {
            if (!sameBay) return `Different bay ${positionSymbol}`;
            return `Vertical dist: ${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          } else if (semanticRelation === 'sameBay') {
            if (!sameBay) return `Different bay ${positionSymbol}`;
            return `Vertical dist: ${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          } else if (semanticRelation === 'sameLevel') {
            return `Vertical dist: ${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          }
        } else if (proximityDirection === 'x-axis') {
          // For Left/Right direction, focus on horizontal relationships
          if (semanticRelation === 'both') {
            if (!sameLevel) return `Different level ${positionSymbol}`;
            return `Horizontal dist: ${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          } else if (semanticRelation === 'sameBay') {
            return `Horizontal dist: ${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          } else if (semanticRelation === 'sameLevel') {
            if (!sameLevel) return `Different level ${positionSymbol}`;
            return `Horizontal dist: ${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          }
        } else {
          // For All Directions, consider vertical position for above/below
          if (semanticRelation === 'above') {
            if (!isAbove) return 'Not above';
            return `Above: B${levelDiff}/L${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          } else if (semanticRelation === 'below') {
            if (!isBelow) return 'Not below';
            return `Below: B${levelDiff}/L${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          } else if (semanticRelation === 'both') {
            if (!sameBay && !sameLevel) {
              return `Dist: B${levelDiff}/L${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
            }
            if (!sameBay) {
              return `Dist: B${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
            }
            if (!sameLevel) {
              return `Dist: L${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
            }
            return `Same bay & level ${positionSymbol} (${distance.toFixed(1)})`;
          } else if (semanticRelation === 'sameBay') {
            if (!sameBay) {
              return `Dist: B${levelDiff}/L${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
            }
            return `Same bay, L${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          } else if (semanticRelation === 'sameLevel') {
            if (!sameLevel) {
              return `Dist: B${levelDiff}/L${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
            }
            return `Same level, B${levelDiff} ${positionSymbol} (${distance.toFixed(1)})`;
          }
        }
        break;
      }

      case 'category': {
        if (location.category !== selectedLoc.category) {
          return `Cat ${location.category}≠${selectedLoc.category} ${positionSymbol}`;
        }

        const verticalInfo = position === 'same' ? '' : ` L${levelDiff}`;

        // Check distance constraints
        if (distance < minDistance) {
          return `Too close${verticalInfo} ${positionSymbol} ${directionText} (${distance.toFixed(1)})`;
        } else if (maxDistance < 10 && distance > maxDistance) {
          return `Too far${verticalInfo} ${positionSymbol} ${directionText} (${distance.toFixed(1)})`;
        } else if (
          skipDistance > 0 &&
          areLocationsAdjacent(selectedLoc, location, proximityDirection)
        ) {
          return `Adjacent${verticalInfo} ${positionSymbol} ${directionText} (skipped)`;
        }

        return `Same Cat ${location.category}${verticalInfo} ${positionSymbol} ${directionText} (${distance.toFixed(1)})`;
      }
    }
    return '';
  };

  // Update areLocationsAdjacent to handle complex relationships
  const areLocationsAdjacent = (loc1, loc2, direction = 'both') => {
    // Calculate bounds for each location
    const bounds1 = {
      left: loc1.col,
      right: loc1.col + loc1.width,
      top: loc1.row,
      bottom: loc1.row + loc1.height,
    };

    const bounds2 = {
      left: loc2.col,
      right: loc2.col + loc2.width,
      top: loc2.row,
      bottom: loc2.row + loc2.height,
    };

    // Check for horizontal adjacency (sharing vertical edge)
    const horizontallyAdjacent =
      (Math.abs(bounds1.right - bounds2.left) < 0.01 ||
        Math.abs(bounds2.right - bounds1.left) < 0.01) &&
      bounds1.top < bounds2.bottom &&
      bounds1.bottom > bounds2.top;

    // Check for vertical adjacency (sharing horizontal edge)
    const verticallyAdjacent =
      (Math.abs(bounds1.bottom - bounds2.top) < 0.01 ||
        Math.abs(bounds2.bottom - bounds1.top) < 0.01) &&
      bounds1.left < bounds2.right &&
      bounds1.right > bounds2.left;

    // Handle direction-specific checks
    if (direction === 'x-axis') {
      return horizontallyAdjacent;
    } else if (direction === 'y-axis') {
      return verticallyAdjacent;
    }

    return horizontallyAdjacent || verticallyAdjacent;
  };

  // Check if location is within distance criteria
  const isWithinDistanceCriteria = distance => {
    // For semantic relationships, round distances to whole numbers
    if (relationshipType === 'semantic') {
      const roundedMin = Math.floor(minDistance);
      const roundedMax = Math.ceil(maxDistance);
      return distance >= roundedMin && distance <= roundedMax;
    }
    return distance >= minDistance && distance <= maxDistance;
  };

  // Check adjacent with skip
  const checkAdjacentWithSkip = (location, otherLoc, skipCount, direction) => {
    if (skipCount === 0) {
      if (areLocationsAdjacent(location, otherLoc, direction)) {
        return { isRelated: true, type: 'adjacent' };
      }
      return { isRelated: false };
    }

    // For skip > 0, we're looking for non-adjacent locations at specific distances
    const adjacentCheck = areLocationsAdjacent(location, otherLoc, direction);
    if (adjacentCheck) {
      return { isRelated: false }; // Skip doesn't apply to adjacent locations
    }

    const distance = calculateDistance(location, otherLoc, direction);
    return {
      isRelated: isWithinDistanceCriteria(distance),
      type: `skip-${skipCount}`,
      distance,
    };
  };

  // Update calculatePhysicalProximity to include vertical position info
  const calculatePhysicalProximity = location => {
    const neighbors = [];
    const lines = [];

    locations.forEach(loc => {
      if (loc.id === location.id) return;

      const distance = calculateDistance(location, loc, proximityDirection);
      const skipCheck = checkAdjacentWithSkip(
        location,
        loc,
        skipDistance,
        proximityDirection,
      );
      const position = getRelativePosition(location, loc);

      if (skipCheck.isRelated && isWithinDistanceCriteria(distance)) {
        neighbors.push({
          ...loc,
          relationshipType: skipCheck.type,
          distance: distance,
          verticalPosition: position,
        });

        // Calculate line endpoints based on direction
        let startPoint, endPoint;

        if (proximityDirection === 'x-axis') {
          startPoint = {
            x: location.col + (loc.col > location.col ? location.width : 0),
            y: location.row + location.height / 2,
          };
          endPoint = {
            x: loc.col + (loc.col > location.col ? 0 : loc.width),
            y: loc.row + loc.height / 2,
          };
        } else if (proximityDirection === 'y-axis') {
          startPoint = {
            x: location.col + location.width / 2,
            y: location.row + (loc.row > location.row ? location.height : 0),
          };
          endPoint = {
            x: loc.col + loc.width / 2,
            y: loc.row + (loc.row > location.row ? 0 : loc.height),
          };
        } else {
          startPoint = {
            x: location.col + location.width / 2,
            y: location.row + location.height / 2,
          };
          endPoint = {
            x: loc.col + loc.width / 2,
            y: loc.row + loc.height / 2,
          };
        }

        lines.push({
          id: `line-${location.id}-${loc.id}`,
          x1: startPoint.x * viewScale,
          y1: startPoint.y * viewScale,
          x2: endPoint.x * viewScale,
          y2: endPoint.y * viewScale,
          type: skipCheck.type,
          distance: distance,
          verticalPosition: position,
        });
      }
    });

    return { neighbors, lines };
  };

  // Calculate semantic proximity relationships
  const calculateSemanticProximity = location => {
    const neighbors = [];
    const lines = [];

    locations.forEach(loc => {
      if (loc.id === location.id) return;

      let isRelated = false;
      let relationType = '';
      const position = getRelativePosition(location, loc);

      // For Top/Bottom direction, consider vertical relationships
      if (proximityDirection === 'y-axis') {
        switch (semanticRelation) {
          case 'above':
            isRelated = position === 'above';
            relationType = 'above';
            break;
          case 'below':
            isRelated = position === 'below';
            relationType = 'below';
            break;
          case 'sameLevel':
          case 'both':
            isRelated = true;
            relationType = 'vertical';
            break;
          case 'sameBay':
            isRelated = loc.bay === location.bay;
            relationType = 'sameBay';
            break;
        }
      } else if (proximityDirection === 'x-axis') {
        // For Left/Right direction, consider horizontal relationships
        switch (semanticRelation) {
          case 'sameLevel':
            isRelated = loc.level === location.level;
            relationType = 'sameLevel';
            break;
          case 'sameBay':
            isRelated = true;
            relationType = 'horizontal';
            break;
          case 'both':
            isRelated = loc.level === location.level;
            relationType = 'both';
            break;
        }
      } else {
        // For All Directions, consider both horizontal and vertical relationships
        switch (semanticRelation) {
          case 'above':
            isRelated = position === 'above';
            relationType = 'above';
            break;
          case 'below':
            isRelated = position === 'below';
            relationType = 'below';
            break;
          case 'sameLevel':
            isRelated = true;
            relationType =
              loc.level === location.level ? 'sameLevel' : 'crossLevel';
            break;
          case 'sameBay':
            isRelated = true;
            relationType = loc.bay === location.bay ? 'sameBay' : 'crossBay';
            break;
          case 'both':
            isRelated = true;
            if (loc.level === location.level && loc.bay === location.bay) {
              relationType = 'both';
            } else if (loc.bay === location.bay) {
              relationType = 'sameBay';
            } else if (loc.level === location.level) {
              relationType = 'sameLevel';
            } else {
              relationType = 'cross';
            }
            break;
        }
      }

      if (isRelated) {
        const distance = calculateDistance(location, loc, proximityDirection);

        // Check if the locations are adjacent (if skip is enabled)
        const isAdjacent = areLocationsAdjacent(
          location,
          loc,
          proximityDirection,
        );

        // Skip adjacent locations if skipDistance > 0
        if (skipDistance > 0 && isAdjacent) {
          return;
        }

        // Check distance criteria
        if (isWithinDistanceCriteria(distance)) {
          neighbors.push({
            ...loc,
            relationshipType: relationType,
            distance: distance,
          });

          // Calculate line endpoints based on direction
          let startPoint, endPoint;

          if (proximityDirection === 'x-axis') {
            startPoint = {
              x: location.col + (loc.col > location.col ? location.width : 0),
              y: location.row + location.height / 2,
            };
            endPoint = {
              x: loc.col + (loc.col > location.col ? 0 : loc.width),
              y: loc.row + loc.height / 2,
            };
          } else if (proximityDirection === 'y-axis') {
            startPoint = {
              x: location.col + location.width / 2,
              y: location.row + (loc.row > location.row ? location.height : 0),
            };
            endPoint = {
              x: loc.col + loc.width / 2,
              y: loc.row + (loc.row > location.row ? 0 : loc.height),
            };
          } else {
            startPoint = {
              x: location.col + location.width / 2,
              y: location.row + location.height / 2,
            };
            endPoint = {
              x: loc.col + loc.width / 2,
              y: loc.row + loc.height / 2,
            };
          }

          lines.push({
            id: `line-${location.id}-${loc.id}`,
            x1: startPoint.x * viewScale,
            y1: startPoint.y * viewScale,
            x2: endPoint.x * viewScale,
            y2: endPoint.y * viewScale,
            type: relationType,
            distance: distance,
          });
        }
      }
    });

    return { neighbors, lines };
  };

  // Update calculateCategoryRelationships to include vertical position info
  const calculateCategoryRelationships = location => {
    const neighbors = [];
    const lines = [];

    locations.forEach(loc => {
      if (loc.id === location.id) return;

      if (loc.category === location.category) {
        const distance = calculateDistance(location, loc, proximityDirection);
        const isAdjacent = areLocationsAdjacent(
          location,
          loc,
          proximityDirection,
        );
        const position = getRelativePosition(location, loc);

        // Check skip distance for adjacent locations
        if (skipDistance > 0 && isAdjacent) {
          return;
        }

        // Check distance criteria
        if (isWithinDistanceCriteria(distance)) {
          neighbors.push({
            ...loc,
            relationshipType: 'sameCategory',
            distance: distance,
            verticalPosition: position,
          });

          // Calculate line endpoints based on direction
          let startPoint, endPoint;

          if (proximityDirection === 'x-axis') {
            startPoint = {
              x: location.col + (loc.col > location.col ? location.width : 0),
              y: location.row + location.height / 2,
            };
            endPoint = {
              x: loc.col + (loc.col > location.col ? 0 : loc.width),
              y: loc.row + loc.height / 2,
            };
          } else if (proximityDirection === 'y-axis') {
            startPoint = {
              x: location.col + location.width / 2,
              y: location.row + (loc.row > location.row ? location.height : 0),
            };
            endPoint = {
              x: loc.col + loc.width / 2,
              y: loc.row + (loc.row > location.row ? 0 : loc.height),
            };
          } else {
            startPoint = {
              x: location.col + location.width / 2,
              y: location.row + location.height / 2,
            };
            endPoint = {
              x: loc.col + loc.width / 2,
              y: loc.row + loc.height / 2,
            };
          }

          lines.push({
            id: `line-${location.id}-${loc.id}`,
            x1: startPoint.x * viewScale,
            y1: startPoint.y * viewScale,
            x2: endPoint.x * viewScale,
            y2: endPoint.y * viewScale,
            type: 'category',
            distance: distance,
            verticalPosition: position,
          });
        }
      }
    });

    return { neighbors, lines };
  };

  // Update relationships
  const updateRelationships = location => {
    if (!location) return;

    let result = { neighbors: [], lines: [] };

    if (relationshipType === 'physical') {
      result = calculatePhysicalProximity(location);
    } else if (relationshipType === 'semantic') {
      result = calculateSemanticProximity(location);
    } else if (relationshipType === 'category') {
      result = calculateCategoryRelationships(location);
    }

    setHighlightedLocations(result.neighbors);
    setRelationshipLines(result.lines);
  };

  // Update relationships when parameters change
  useEffect(() => {
    if (selectedLocation) {
      updateRelationships(selectedLocation);
    }

    // For semantic relationships, ensure distances are whole numbers
    if (relationshipType === 'semantic') {
      setMinDistance(Math.floor(minDistance));
      setMaxDistance(Math.ceil(maxDistance));
    }
  }, [
    relationshipType,
    proximityDirection,
    semanticRelation,
    skipDistance,
    minDistance,
    maxDistance,
  ]);

  // Handle location click
  const handleLocationClick = location => {
    setSelectedLocation(location);
    updateRelationships(location);
  };

  // Get category color class
  const getCategoryColor = category => {
    switch (category) {
      case 'A':
        return 'bg-blue-400';
      case 'B':
        return 'bg-green-400';
      case 'C':
        return 'bg-yellow-400';
      case 'D':
        return 'bg-red-400';
      case 'E':
        return 'bg-purple-400';
      case 'F':
        return 'bg-pink-400';
      case 'G':
        return 'bg-indigo-400';
      case 'H':
        return 'bg-cyan-400';
      default:
        return 'bg-gray-400';
    }
  };

  // Get category text color
  const getCategoryTextColor = category => {
    switch (category) {
      case 'A':
        return 'text-blue-800';
      case 'B':
        return 'text-green-800';
      case 'C':
        return 'text-yellow-800';
      case 'D':
        return 'text-red-800';
      case 'E':
        return 'text-purple-800';
      case 'F':
        return 'text-pink-800';
      case 'G':
        return 'text-indigo-800';
      case 'H':
        return 'text-cyan-800';
      default:
        return 'text-gray-800';
    }
  };

  // Calculate style for a location
  const calculateStyle = location => {
    return {
      left: `${location.col * viewScale}px`,
      top: `${location.row * viewScale}px`,
      width: `${location.width * viewScale}px`,
      height: `${location.height * viewScale}px`,
    };
  };

  // Update getLineStyle to use vertical position colors in all modes
  const getLineStyle = (type, verticalPosition) => {
    let baseStyle;

    // First determine the base style based on type
    const typeStyle =
      {
        adjacent: 'stroke-blue-500',
        'skip-1': 'stroke-blue-500 stroke-dasharray-2',
        'skip-2': 'stroke-blue-500 stroke-dasharray-4',
        'skip-3': 'stroke-blue-500 stroke-dasharray-6',
        level: 'stroke-red-500',
        bay: 'stroke-orange-500',
        both: 'stroke-pink-500',
        category: 'stroke-purple-500',
        vertical: 'stroke-cyan-500',
        horizontal: 'stroke-emerald-500',
        cross: 'stroke-violet-500',
        above: 'stroke-cyan-500',
        below: 'stroke-emerald-500',
        default: 'stroke-gray-500',
      }[type] || 'stroke-gray-500';

    // Then modify based on vertical position if needed
    if (relationshipType !== 'semantic' && verticalPosition) {
      if (verticalPosition === 'above') {
        baseStyle = 'stroke-cyan-500';
      } else if (verticalPosition === 'below') {
        baseStyle = 'stroke-emerald-500';
      } else {
        baseStyle = typeStyle;
      }
    } else {
      baseStyle = typeStyle;
    }

    return `${baseStyle} stroke-2`;
  };

  // Calculate shelf dimensions - account for bay spacing
  const baySpacing = 1.5; // Increased from 1.2 to add more margin between bays
  const shelfWidth = bayCount * baySpacing * viewScale;
  const shelfHeight = levelCount * viewScale;

  // Determine if a location should be faded based on selection
  const shouldFadeLocation = location => {
    if (!selectedLocation) return false;

    const isSelected = selectedLocation.id === location.id;
    const isHighlighted = highlightedLocations.some(
      loc => loc.id === location.id,
    );

    return !isSelected && !isHighlighted;
  };

  // Reset configuration to defaults
  const resetConfig = () => {
    setBayCount(5);
    setLevelCount(5);
    setLocationSizeVarietyCount(3);
    setCategoryCount(3);
    setViewScale(100);
  };

  // Update location rendering to show bay position
  const getLocationPositionInBay = location => {
    // Find all locations in the same bay and level
    const sameLevel = locations.filter(
      loc => loc.bay === location.bay && loc.level === location.level,
    );

    // Sort by column position
    const sorted = [...sameLevel].sort((a, b) => a.col - b.col);

    // Find position (1-based index)
    return sorted.findIndex(loc => loc.id === location.id) + 1;
  };

  return (
    <div className="p-4">
      <h2 className="mb-4 text-xl font-bold">
        Location Relationships Visualization
      </h2>

      <div className="bg-menu/50 mb-4 grid grid-cols-1 gap-4 rounded p-3 md:grid-cols-3">
        <div>
          <h3 className="mb-1 text-sm font-semibold">Relationship Type:</h3>
          <div className="mb-1 flex flex-wrap gap-2">
            <button
              className={`rounded px-3 py-1 text-xs ${relationshipType === 'physical' ? 'bg-blue-500 text-white' : 'bg-menu/60'}`}
              onClick={() => setRelationshipType('physical')}
            >
              Physical
            </button>
            <button
              className={`rounded px-3 py-1 text-xs ${relationshipType === 'semantic' ? 'bg-blue-500 text-white' : 'bg-menu/60'}`}
              onClick={() => setRelationshipType('semantic')}
            >
              Semantic
            </button>
            <button
              className={`rounded px-3 py-1 text-xs ${relationshipType === 'category' ? 'bg-blue-500 text-white' : 'bg-menu/60'}`}
              onClick={() => setRelationshipType('category')}
            >
              Category
            </button>
          </div>
        </div>

        <div>
          <h3 className="mb-1 text-sm font-semibold">Direction:</h3>
          <div className="mb-1 flex flex-wrap gap-2">
            <button
              className={`rounded px-3 py-1 text-xs ${proximityDirection === 'both' ? 'bg-blue-500 text-white' : 'bg-menu/60'}`}
              onClick={() => setProximityDirection('both')}
            >
              All Directions
            </button>
            <button
              className={`rounded px-3 py-1 text-xs ${proximityDirection === 'x-axis' ? 'bg-blue-500 text-white' : 'bg-menu/60'}`}
              onClick={() => setProximityDirection('x-axis')}
            >
              Left/Right
            </button>
            <button
              className={`rounded px-3 py-1 text-xs ${proximityDirection === 'y-axis' ? 'bg-blue-500 text-white' : 'bg-menu/60'}`}
              onClick={() => setProximityDirection('y-axis')}
            >
              Top/Bottom
            </button>
          </div>
        </div>

        {relationshipType === 'semantic' && (
          <div>
            <h3 className="mb-1 text-sm font-semibold">Semantic Type:</h3>
            <div className="mb-1 flex flex-wrap gap-2">
              <button
                className={`rounded px-3 py-1 text-xs ${semanticRelation === 'sameLevel' ? 'bg-red-500 text-white' : 'bg-menu/60'}`}
                onClick={() => setSemanticRelation('sameLevel')}
              >
                Same Level
              </button>
              <button
                className={`rounded px-3 py-1 text-xs ${semanticRelation === 'sameBay' ? 'bg-orange-500 text-white' : 'bg-menu/60'}`}
                onClick={() => setSemanticRelation('sameBay')}
              >
                Same Bay
              </button>
              <button
                className={`rounded px-3 py-1 text-xs ${semanticRelation === 'both' ? 'bg-pink-500 text-white' : 'bg-menu/60'}`}
                onClick={() => setSemanticRelation('both')}
              >
                Both
              </button>
              <button
                className={`rounded px-3 py-1 text-xs ${semanticRelation === 'above' ? 'bg-cyan-500 text-white' : 'bg-menu/60'}`}
                onClick={() => setSemanticRelation('above')}
              >
                Above
              </button>
              <button
                className={`rounded px-3 py-1 text-xs ${semanticRelation === 'below' ? 'bg-emerald-500 text-white' : 'bg-menu/60'}`}
                onClick={() => setSemanticRelation('below')}
              >
                Below
              </button>
            </div>
          </div>
        )}

        <div className="col-span-1 md:col-span-2">
          <h3 className="mb-1 text-sm font-semibold">Distance Range:</h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs">Min:</span>
              <input
                type="range"
                min="0"
                max="5"
                step="0.5"
                value={minDistance}
                onChange={e => setMinDistance(parseFloat(e.target.value))}
                className="w-24"
              />
              <span className="text-xs font-medium">
                {minDistance === 0 ? 'Adjacent' : minDistance.toFixed(1)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs">Max:</span>
              <input
                type="range"
                min="0.5"
                max="10"
                step="0.5"
                value={maxDistance}
                onChange={e => setMaxDistance(parseFloat(e.target.value))}
                className="w-24"
              />
              <span className="text-xs font-medium">
                {maxDistance >= 10 ? '∞' : maxDistance.toFixed(1)}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="mb-1 text-sm font-semibold">
                  Skip Distance:
                </span>

                <input
                  type="range"
                  min="0"
                  max="3"
                  value={skipDistance}
                  onChange={e => setSkipDistance(parseInt(e.target.value))}
                  className="w-32"
                />
                <span className="text-xs font-medium">
                  {skipDistance === 0 ? 'Adjacent' : `Skip ${skipDistance}`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* <div>
          <h3 className="text-sm font-semibold">Visualization:</h3>
          <div className="mt-1">
            <button
              className={`rounded px-3 py-1 text-xs ${showHeatmap ? 'bg-blue-500 text-white' : 'bg-menu/60'}`}
              onClick={() => setShowHeatmap(!showHeatmap)}
            >
              {showHeatmap ? 'Hide' : 'Show'} Distance Heatmap
            </button>
          </div>
        </div> */}
      </div>

      <div
        data-component="location-grid"
        className="bg-menu/70 relative mb-4 flex w-full flex-col items-center justify-center overflow-auto pt-10"
        style={{
          height: `${Math.min(600, shelfHeight + 40)}px`,
          width: '100%',
        }}
      >
        <div
          className="relative mx-auto"
          style={{
            height: `${shelfHeight}px`,
            width: `${shelfWidth}px`,
            maxWidth: '100%',
          }}
        >
          {/* Bay margins (vertical spacing between bays) */}
          {shelfStructure.bays.map(
            (bay, index) =>
              index > 0 && (
                <div
                  key={`bay-margin-${index}`}
                  className="absolute h-full bg-gray-300"
                  style={{
                    left: `${shelfStructure.bays[index - 1].endCol * viewScale}px`,
                    width: `${(bay.startCol - shelfStructure.bays[index - 1].endCol) * viewScale}px`,
                  }}
                />
              ),
          )}

          {/* Level horizontal dividers */}
          {shelfStructure.levels.map(
            (level, index) =>
              index > 0 && (
                <div
                  key={`level-divider-${index}`}
                  className="absolute w-full border-t-2 border-white"
                  style={{ top: `${level.startRow * viewScale}px` }}
                />
              ),
          )}

          {/* Bay headers */}
          {shelfStructure.bays.map((bay, index) => (
            <div
              key={`bay-header-${index}`}
              className="absolute p-1 text-center text-xs font-bold text-white"
              style={{
                left: `${bay.startCol * viewScale}px`,
                width: `${viewScale}px`,
                top: '-20px',
                backgroundColor: bay.color,
              }}
            >
              {bay.id}
            </div>
          ))}

          {/* Level background colors */}
          {shelfStructure.levels.map((level, levelIndex) => (
            <div
              key={`level-bg-${levelIndex}`}
              className="absolute w-full"
              style={{
                top: `${level.startRow * viewScale}px`,
                height: `${(level.endRow - level.startRow) * viewScale}px`,
                backgroundColor: level.color,
              }}
            ></div>
          ))}

          {/* Relationship lines */}
          <svg className="pointer-events-none absolute left-0 top-0 z-10 h-full w-full">
            {relationshipLines.map(line => (
              <line
                key={line.id}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                className={getLineStyle(line.type, line.verticalPosition)}
              />
            ))}
          </svg>

          {/* Locations */}
          {locations.map(location => {
            const isSelected =
              selectedLocation && selectedLocation.id === location.id;
            const isHighlighted = highlightedLocations.some(
              loc => loc.id === location.id,
            );
            const shouldFade = shouldFadeLocation(location);
            const categoryColorClass = getCategoryColor(location.category);

            // Calculate opacity based on selection state in category mode
            const opacity =
              relationshipType === 'category'
                ? isSelected
                  ? '1'
                  : isHighlighted
                    ? '0.9'
                    : '0.3'
                : shouldFade
                  ? '0.3'
                  : '1';

            const locationRelType = isHighlighted
              ? highlightedLocations.find(loc => loc.id === location.id)
                  ?.relationshipType
              : null;

            // Generate location label
            const bayId = location.bay.split('-')[1];
            const levelId = location.level.split('-')[1];
            const positionId = getLocationPositionInBay(location)
              .toString()
              .padStart(2, '0');
            const locationLabel = `${bayId}-${positionId}-${levelId}`;

            return (
              <div
                key={location.id}
                className={`absolute flex flex-col items-center justify-center ${
                  isSelected ? 'z-30 font-bold ring-4 ring-blue-600' : ''
                } ${
                  isHighlighted ? 'ring-3 z-20 ring-purple-500' : ''
                } overflow-hidden border-2 border-black transition-opacity duration-300 ${categoryColorClass}`}
                style={{
                  ...calculateStyle(location),
                  opacity,
                  backgroundColor: getHeatmapColor(location, selectedLocation),
                }}
                onClick={() => handleLocationClick(location)}
              >
                <div className="max-w-full truncate px-1 text-sm font-semibold">
                  {locationLabel}
                </div>
                <div className="text-xs font-bold">Cat {location.category}</div>

                {/* Relationship description */}
                {selectedLocation && (
                  <div
                    className={`px-1 text-center text-xs ${
                      isHighlighted
                        ? 'font-bold text-white'
                        : 'text-menu-text/50'
                    }`}
                  >
                    {getRelationshipDescription(location, selectedLocation)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedLocation && (
        <div className="bg-menu/70 mt-4 rounded-lg p-3">
          <div className="flex justify-between">
            <h3 className="mb-2 text-lg font-bold">
              Selected: {selectedLocation.id}
            </h3>
            <button
              className="bg-menu/60 hover:bg-menu-300 rounded px-2 py-1 text-xs"
              onClick={() => {
                setSelectedLocation(null);
                setHighlightedLocations([]);
                setRelationshipLines([]);
              }}
            >
              Clear Selection
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <p>
                <span className="font-semibold">Category:</span>{' '}
                {selectedLocation.category}
              </p>
              <p>
                <span className="font-semibold">Bay:</span>{' '}
                {selectedLocation.bay}
              </p>
              <p>
                <span className="font-semibold">Level:</span>{' '}
                {selectedLocation.level}
              </p>
              <p>
                <span className="font-semibold">Size:</span>{' '}
                {selectedLocation.width.toFixed(1)} x{' '}
                {selectedLocation.height.toFixed(1)}
              </p>
              <p>
                <span className="font-semibold">Position:</span> (
                {selectedLocation.col.toFixed(1)},{' '}
                {selectedLocation.row.toFixed(1)})
              </p>
            </div>
            <div>
              <h4 className="font-semibold">
                Relationships (
                {proximityDirection === 'both'
                  ? 'All Directions'
                  : proximityDirection === 'x-axis'
                    ? 'Left/Right'
                    : 'Top/Bottom'}
                ):
              </h4>
              {highlightedLocations.length > 0 ? (
                <div className="max-h-40 overflow-y-auto">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr>
                        <th className="py-1 text-left">Location</th>
                        <th className="py-1 text-left">Type</th>
                        <th className="py-1 text-right">Distance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {highlightedLocations.map(loc => (
                        <tr key={loc.id} className="border-t border-gray-200">
                          <td className="py-1">{loc.id}</td>
                          <td className="py-1">{loc.relationshipType}</td>
                          <td className="py-1 text-right">
                            {loc.distance.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm italic">
                  No relationships found with current settings.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-menu/50 mb-4 grid grid-cols-1 gap-4 rounded p-3 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <h3 className="text-sm font-semibold">Shelf Configuration:</h3>
          <div className="mt-1 grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-600">Bay Count</label>
              <input
                type="range"
                min="2"
                max="10"
                value={bayCount}
                onChange={e => setBayCount(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="text-center text-xs font-medium">{bayCount}</div>
            </div>
            <div>
              <label className="text-xs text-gray-600">Level Count</label>
              <input
                type="range"
                min="2"
                max="10"
                value={levelCount}
                onChange={e => setLevelCount(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="text-center text-xs font-medium">
                {levelCount}
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold">Location Variety:</h3>
          <div className="mt-1 grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-600">Size Variations</label>
              <input
                type="range"
                min="1"
                max="10"
                value={locationSizeVarietyCount}
                onChange={e =>
                  setLocationSizeVarietyCount(parseInt(e.target.value))
                }
                className="w-full"
              />
              <div className="text-center text-xs font-medium">
                {locationSizeVarietyCount}
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-600">Categories (A-Z)</label>
              <input
                type="range"
                min="1"
                max="8"
                value={categoryCount}
                onChange={e => setCategoryCount(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="text-center text-xs font-medium">
                {categoryCount}
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold">View Scale:</h3>
          <div className="mt-1">
            <input
              type="range"
              min="80"
              max="150"
              value={viewScale}
              onChange={e => setViewScale(parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-xs font-medium">{viewScale}%</div>
          </div>
          <button
            onClick={resetConfig}
            className="bg-menu/60 text-menu-text/50 mt-1 w-full rounded px-2 py-1 text-xs hover:bg-gray-300"
          >
            Reset Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationRelationship;
